/**
 * session.ts — owns the lifecycle of a single in-game session.
 *
 * Extracted from main.ts during v2M1 when the app gained a main menu.
 * Before v2M1 the app booted directly to gameplay and never tore down;
 * with the main menu + "Return to Main Menu" option, every in-game mount
 * + the render/sim loops need a coordinated teardown path.
 *
 * Lifecycle:
 *   const session = startGameSession(deps)   // mounts HUD, panels, loops
 *   ...                                      // gameplay runs
 *   session.destroy()                        // stops loops, unmounts UI
 *
 * After destroy(), the canvas and the LoadedGameData are still alive —
 * they belong to the outer main.ts shell and are reused across sessions.
 */

import type { LoadedGameData } from '../data/loader';
import type { Command } from '../game/commands';
import { startSimLoop } from '../game/loop';
import { computeViewport, initRenderer, renderFrame } from '../render/canvas';
import { loadGalaxyOverlay } from '../render/galaxy-overlays';
import { mountHud } from './hud';
import { mountOverlayToggleWidget } from './overlay-toggle-widget';
import { mountAlertFeed } from './alert-feed';
import { mountDetailPanel } from './detail-panel';
import { mountInGameMenu, type InGameMenuHandle } from './in-game-menu';
import {
  mountEndGameOverlay,
  type EndGameOverlayHandle,
} from './end-game-overlay';
import { mountMiniGalaxy } from './mini-galaxy';
import { mountJpTooltip } from './jp-tooltip';
import { mountFleetCompositionTooltip } from './fleet-composition-tooltip';
import { mountResearchPanel } from './research-panel';
import { mountDevToolbox } from './dev-toolbox';
import { mountFleetPanel } from './fleet-panel';
import { attachInputHandlers } from './input';
import { createSpeedController } from '../game/speed-controller';
import { mountSpeedWidget } from './speed-widget';
import { createUiState } from './state';
import type { Settings } from '../settings/types';
import type { GameState } from '../state/types/index';
import { getAudioEngine } from '../audio';
import {
  createCombatOverlayState,
  tearDownCombatOverlay,
  updateCombatOverlay,
} from '../audio/combat-overlay';

export interface StartGameSessionDeps {
  canvas: HTMLCanvasElement;
  data: LoadedGameData;
  /**
   * Pre-built GameState. Caller decides whether this is a fresh scenario
   * (createInitialGameState) or a deserialised save (deserializeGameState);
   * the session is agnostic. Decoupled in v2M2 so the Load flow can reuse
   * the same session pipeline as a new game.
   */
  state: GameState;
  /**
   * Called when the player picks "Return to Main Menu" from the in-game
   * menu. Session applies the v2M4 confirmReturnToMainMenu gate before
   * invoking this callback.
   */
  onReturnToMainMenu: () => void;
  /**
   * Called when the player picks "Exit" from the in-game menu. Session
   * applies the v2M4 confirmExit gate before invoking this callback.
   */
  onExit: () => void;
  /**
   * Called when the player picks "Save" from the in-game menu (v2M2).
   * Receives a getter for the current sim state so the caller can
   * serialise it without holding a stale reference. Optional — sessions
   * that don't wire saving (e.g. test harnesses) can omit it.
   */
  onSave?: (getState: () => GameState) => void;
  /**
   * v4M1.4 — autosave dispatch hook. Session fires this when the
   * tick-driven autosave interval elapses while autosave is enabled.
   * main.ts wires it to write to the dedicated `'autosave'` storage
   * slot. Session does NOT pause for the write — it's fast and
   * synchronous (localStorage). Optional so test harnesses without a
   * storage layer can omit it.
   */
  onAutosave?: (getState: () => GameState) => void;
  /**
   * v4M1.4 — combined "Save & Return to Main Menu" hook. Opens the
   * save picker; on successful save, tears down the session and
   * returns to the main menu without prompting for confirmation. main.ts
   * is responsible for both the picker mount and the post-save
   * transition. Optional so headless harnesses can omit it.
   */
  onSaveAndReturn?: (getState: () => GameState) => void;
  /**
   * Called when the player picks "Settings" from the in-game menu (v2M4).
   * main.ts mounts the Settings overlay; session does not own it because
   * Settings is also reachable from the main-menu and lives at the app level.
   */
  onSettings?: () => void;
  /**
   * Live settings reference used by the session for:
   *  - seeding the SpeedController's initialSpeed (v2M3 + v2M4),
   *  - gating the Return-to-Main-Menu and Exit confirm prompts (v2M4).
   *
   * The reference is read fresh on each prompt so a settings change
   * mid-session takes effect immediately (no need to re-create the session).
   */
  getSettings?: () => Settings;
}

export interface SessionHandle {
  destroy(): void;
}

export function startGameSession(deps: StartGameSessionDeps): SessionHandle {
  const {
    canvas,
    state,
    onReturnToMainMenu,
    onExit,
    onSave,
    onAutosave,
    onSaveAndReturn,
    onSettings,
    getSettings,
  } = deps;

  // v4M1.4 — autosave tick tracker. Initialised to the state's current
  // tick so the first autosave fires `autosaveFrequencyTicks` ticks
  // after session start (rather than firing immediately on a fresh
  // game or right after loading). Updated after every successful
  // autosave dispatch.
  let lastAutosaveTick = state.tick;
  // v4M1.12 — autosave-failure spam guard. Without this, a persistent
  // failure (localStorage quota exhausted, serialisation crash, etc.)
  // would log a warn every `autosaveFrequencyTicks` and either spam
  // the console or — worse — induce render-loop pressure. Logs the
  // first failure verbatim, suppresses subsequent identical failures,
  // then re-arms on the next successful save so a transient failure
  // followed by a recovery surfaces both events.
  let autosaveFailureLogged = false;

  const ui = createUiState();
  // v4M6.2 — restore the player's last-saved strategic-overlay choice
  // (Production / Threat / Exploration / Diplomatic / None) so the
  // selection survives a page reload. Silent fallback to `'none'` when
  // storage is unavailable or the stored value is corrupted.
  ui.galaxyOverlay = loadGalaxyOverlay();

  const rc = initRenderer(canvas);
  const hud = mountHud(document.body);
  // v4M6.2 — strategic overlay toggle row (None / Production / Threat
  // / Exploration / Diplomatic). Mirrors the speed-widget shape; sits
  // just below the top HUD bar on the left so it doesn't overlap the
  // mini-galaxy (bottom-right) or the right-side detail panel.
  const overlayToggle = mountOverlayToggleWidget(document.body, ui);
  // v2M8.7 — kick off the ambient music bed for this session. Idempotent
  // on the engine side; session destroy fades it out.
  getAudioEngine().startAmbientBed();
  // v4M1.7 — combat-overlay state machine. Per-frame update reads
  // the sim's combat-event buffer and fades the combat-overlay music
  // bed in / out via the AudioEngine. Owned here so a session
  // teardown can wipe the bed cleanly without the engine carrying
  // stale state across Return-to-Main-Menu.
  const combatOverlayState = createCombatOverlayState();
  // v2M8.3 — alert feed (UI_UX §1.5 + §7). Player-only viewer scope:
  // F10 enemy-POV doesn't remap which empire's events surface. Click
  // on a toast with a `source` jumps the camera to that target.
  const alertFeed = mountAlertFeed(document.body, {
    onJumpTo: (source) => {
      if (source.kind === 'planet') {
        const planet = state.planets.get(source.id);
        if (planet) {
          ui.focusedSystemId = planet.systemId;
          ui.viewMode = 'system';
        }
      } else if (source.kind === 'ship') {
        const ship = state.ships.get(source.id);
        if (ship && ship.systemId !== null) {
          ui.focusedSystemId = ship.systemId;
          ui.viewMode = 'system';
        }
      } else if (source.kind === 'system') {
        ui.focusedSystemId = source.id;
        ui.viewMode = 'system';
      }
    },
    // v4M1.5 — feed reads max-visible + muted-sources from Settings.
    getInterfaceSettings: getSettings
      ? () => getSettings().interface
      : undefined,
  });
  // Speed widget mount happens after the controller exists (later in the
  // function); reference declared here but assigned after createSpeedController.

  // Sim loop is started below; create a forward reference so both the input
  // handler and the detail panel can queue commands into it
  // (input / UI click → command queue → sim tick).
  let queueCommandRef: ((cmd: Command) => void) | null = null;
  const dispatchCommand = (cmd: Command): void => {
    queueCommandRef?.(cmd);
  };

  const panel = mountDetailPanel(document.body, { queueCommand: dispatchCommand });
  // v4M10.3 — mini-galaxy no longer needs an alert-feed bridge: the
  // v4M6.5 unscouted-system drill-in gate is retired per `FOW.md §3.4`.
  // The alert-feed itself stays mounted for combat / command-rejection
  // events; the per-frame `alertFeed.update()` call below still drives
  // it.
  const miniGalaxy = mountMiniGalaxy(document.body);
  const jpTooltip = mountJpTooltip(document.body);
  // v4M6.6 — fleet-composition tooltip mirrors the jp-tooltip mount.
  // Activates when the player hovers an in-flight ship dot in galaxy
  // view; shows chassis breakdown + destination + ETA for own /
  // teammate fleets, degraded "Enemy fleet (N ships)" for visible
  // enemy fleets per `MAP.md §4.3.2` intel asymmetry.
  const fleetCompositionTooltip = mountFleetCompositionTooltip(document.body);
  const researchPanel = mountResearchPanel(document.body, {
    queueCommand: dispatchCommand,
  });
  const devToolbox = mountDevToolbox(document.body, { uiState: ui });
  const fleetPanel = mountFleetPanel(document.body);

  // Start the sim loop. Initial speed dispatched by the controller below
  // — startSimLoop opts.initialSpeed=0 so we don't double-emit before
  // the controller takes over.
  const simLoop = startSimLoop(state, { initialSpeed: 0 });
  queueCommandRef = (cmd) => simLoop.queueCommand(cmd);

  // Speed controller (v2M3) — single source of truth for sim speed,
  // combining the player-driven user pause + speed ladder with the
  // overlay-driven menu pause. Effective speed flows through here to
  // simLoop.setSpeed. Initial speed seeded from settings.gameplay
  // .defaultGameSpeed (v2M4) so a player who set "default 4×" lands at
  // 4× when they start or load a game.
  const initialSpeed = getSettings
    ? getSettings().gameplay.defaultGameSpeed
    : 1;
  const speedController = createSpeedController({
    setSpeed: (effective) => simLoop.setSpeed(effective),
    initialSpeed,
  });
  const speedWidget = mountSpeedWidget(document.body, speedController);

  // v4M1.10 — Auto-pause on focus loss. When the `pauseOnFocusLoss`
  // Gameplay setting is enabled (default), losing window focus (tab
  // switch, browser minimise) pauses the sim; regaining focus resumes.
  // The setting is read on each blur/focus event so toggling it mid-
  // game takes effect immediately. Handlers detach on session teardown
  // so a teardown-while-blurred doesn't leave a stale closure call
  // against a dead controller.
  const onWindowBlur = (): void => {
    const enabled = getSettings?.().gameplay.pauseOnFocusLoss ?? true;
    if (!enabled) return;
    speedController.pauseForFocusLoss();
  };
  const onWindowFocus = (): void => {
    // Always resume on focus return — even if the setting was disabled
    // mid-blur, we don't want to leave the controller paused
    // permanently because the pause was set when the setting was on.
    speedController.resumeFromFocusLoss();
  };
  window.addEventListener('blur', onWindowBlur);
  window.addEventListener('focus', onWindowFocus);

  // In-game menu state. The menu is mounted on demand when Esc is pressed
  // with nothing to dismiss. While the menu is open, the sim loop is
  // paused via the speed controller's menu-pause path so the player can
  // make a decision without the world advancing.
  let inGameMenu: InGameMenuHandle | null = null;
  // v3M12.2 — end-game overlay handle. Mounted on outcome flip (the
  // render-loop poll below); held in scope so destroy() can tear it
  // down on session teardown. `endGameContinued` records that the
  // player clicked Continue Playing — keeps the overlay from
  // re-mounting on every frame after dismissal.
  let endGameOverlay: EndGameOverlayHandle | null = null;
  let endGameContinued = false;

  const openInGameMenu = (): void => {
    if (inGameMenu !== null) return;
    speedController.pauseForMenu();
    inGameMenu = mountInGameMenu(document.body, {
      onResume: closeInGameMenu,
      onSaveGame: onSave
        ? () => {
            // The save flow (slot picker) is owned by main.ts. It runs
            // while the menu remains open so the picker can layer on top;
            // closing here would unpause the sim mid-decision. The save
            // host is responsible for calling closeInGameMenu when it
            // finishes (cancel or confirm) — see main.ts onSave wiring.
            onSave(() => state);
          }
        : undefined,
      onSaveAndReturn: onSaveAndReturn
        ? () => {
            // v4M1.4 — same picker-on-top pattern as onSaveGame. The
            // host (main.ts) decides post-save behaviour: on a
            // successful save, tear down the session and enter the
            // main menu. No confirm gate — the player's explicit
            // Save & Return click is the consent.
            onSaveAndReturn(() => state);
          }
        : undefined,
      onSettings: () => {
        // Mount the Settings overlay on top of the menu. Owned by main.ts
        // because Settings is also reachable from the main menu and the
        // overlay shouldn't be re-implemented here.
        onSettings?.();
      },
      onReturnToMainMenu: () => {
        const shouldConfirm =
          getSettings?.().gameplay.confirmReturnToMainMenu ?? true;
        if (shouldConfirm && !confirm('Discard current game and return to main menu?')) {
          return;
        }
        // No un-pause; the session is about to be torn down.
        onReturnToMainMenu();
      },
      onConcede: () => {
        // v3M12.4 — manual surrender. Confirm gate is unconditional
        // (no Settings toggle in v1 — surrender is rarer + more
        // consequential than return-to-menu). Dispatches the concede
        // command which flips gameOutcome to 'defeat' on the next sim
        // tick before the freeze guard takes hold; the render loop
        // then mounts the end-game overlay automatically.
        if (!confirm('Concede the game? Your empire will be marked as defeated.')) {
          return;
        }
        dispatchCommand({ kind: 'concede' });
        closeInGameMenu();
      },
      onExit: () => {
        const shouldConfirm = getSettings?.().gameplay.confirmExit ?? true;
        if (shouldConfirm && !confirm('Exit Strategos Universalis?')) return;
        onExit();
      },
    });
  };

  const closeInGameMenu = (): void => {
    if (inGameMenu === null) return;
    inGameMenu.destroy();
    inGameMenu = null;
    speedController.resumeFromMenu();
  };

  const input = attachInputHandlers({
    canvas,
    uiState: ui,
    getGameState: () => state,
    getViewport: () => computeViewport(canvas, ui),
    queueCommand: dispatchCommand,
    onOpenMenu: openInGameMenu,
    speedController,
    // v4M1.5 — controls settings drive smooth-zoom speed + hold-G mode.
    getControlsSettings: getSettings
      ? () => getSettings().controls
      : undefined,
  });

  // rAF-driven render at display rate, independent of sim tick rate.
  //
  // The loop body is wrapped in try/catch so a bug in any one frame (e.g. a
  // renderer ReferenceError) logs to console instead of killing the rAF
  // loop and leaving the canvas permanently frozen.
  let renderRunning = true;
  let renderErrorLogged = false;
  const renderLoop = (): void => {
    if (!renderRunning) return;
    try {
      // v3M19.3 — fetch the floating-damage-numbers gameplay toggle
      // each frame so a Settings save mid-session takes effect on the
      // very next frame without a session restart. Defaults to `true`
      // when no getSettings was injected (test scaffolds, ad-hoc embed).
      const showDamageNumbers =
        getSettings?.().gameplay.showDamageNumbers ?? true;
      renderFrame(rc, state, ui, { showDamageNumbers });
      hud.update(state);
      speedWidget.update();
      overlayToggle.update();
      panel.update(state, ui);
      miniGalaxy.update(state, ui);
      jpTooltip.update(state, ui);
      fleetCompositionTooltip.update(state, ui);
      researchPanel.update(state);
      devToolbox.update(state);
      fleetPanel.update(state, ui);
      alertFeed.update(state, state.playerEmpireId);
      // v4M1.7 — combat-overlay state machine. Reads the per-tick
      // combat-event buffer and ramps the overlay bed accordingly.
      // Pause-aware: skips when state.tick hasn't advanced.
      updateCombatOverlay(state, getAudioEngine(), combatOverlayState);
      // v4M1.4 — tick-driven autosave dispatch. Fires when:
      //   1. an `onAutosave` callback was wired by main.ts,
      //   2. `gameplay.autosaveEnabled` is on (settings),
      //   3. the game is still in progress (no autosave of a frozen
      //      victory/defeat state — the player has already seen the
      //      end-game overlay and a fresh autosave just past the flip
      //      would overwrite a useful pre-resolution snapshot),
      //   4. the elapsed ticks since the last autosave clear the
      //      configured frequency.
      // Pause behaviour is implicit: `state.tick` stops advancing
      // while the sim is paused, so the inequality never triggers
      // until ticks resume.
      if (
        onAutosave !== undefined &&
        state.gameOutcome === 'in-progress'
      ) {
        const g = getSettings?.().gameplay;
        const enabled = g?.autosaveEnabled ?? true;
        const frequency = g?.autosaveFrequencyTicks ?? 3000;
        if (enabled && state.tick - lastAutosaveTick >= frequency) {
          try {
            onAutosave(() => state);
            // v4M1.12 — successful save re-arms the failure-log flag
            // so a future transient failure surfaces afresh rather
            // than being silently suppressed.
            autosaveFailureLogged = false;
          } catch (err) {
            // v4M1.12 — log the first failure verbatim, suppress
            // subsequent failures until the next successful save.
            // Localstorage quota / serialisation crashes shouldn't
            // kill the render loop OR spam the console once they
            // recur every `autosaveFrequencyTicks`.
            if (!autosaveFailureLogged) {
              console.warn('Autosave failed (further failures suppressed until recovery):', err);
              autosaveFailureLogged = true;
            }
          }
          lastAutosaveTick = state.tick;
        }
      }
      // v3M12.2 — mount the end-game overlay the first frame the
      // outcome flips. Re-mount-after-Continue is suppressed by
      // `endGameContinued`. Outcome flips are one-way; once mounted,
      // the overlay stays unless explicitly dismissed.
      if (
        endGameOverlay === null &&
        !endGameContinued &&
        state.gameOutcome !== 'in-progress'
      ) {
        // v3M12.3 — fire the audio stinger alongside the overlay
        // mount. Engine handles the 1 s ambient fade + 1 s acoustic
        // gap so the stinger lands cleanly even from a busy combat
        // moment.
        getAudioEngine().playEndGameStinger(
          state.gameOutcome === 'victory' ? 'music:victory' : 'music:defeat',
        );
        endGameOverlay = mountEndGameOverlay(
          document.body,
          state,
          state.gameOutcome,
          {
            onReturnToMainMenu: () => {
              if (endGameOverlay !== null) {
                endGameOverlay.destroy();
                endGameOverlay = null;
              }
              onReturnToMainMenu();
            },
            onContinue: () => {
              endGameContinued = true;
              if (endGameOverlay !== null) {
                endGameOverlay.destroy();
                endGameOverlay = null;
              }
            },
          },
        );
      }
    } catch (err) {
      // Log the first error loudly; keep looping so later frames can still
      // draw if the bug is transient. Subsequent errors are summarised to
      // avoid console spam.
      if (!renderErrorLogged) {
        console.error('Render loop error (loop will continue):', err);
        renderErrorLogged = true;
      } else {
        console.warn('Render loop error (suppressed)');
      }
    }
    requestAnimationFrame(renderLoop);
  };
  requestAnimationFrame(renderLoop);

  // Debug handles on window — removed in a later milestone. Session-scoped
  // so Return-to-Main-Menu drops stale references.
  Object.assign(window as unknown as Record<string, unknown>, {
    __state: state,
    __ui: ui,
  });

  console.info(
    `Strategos Universalis booted. ${state.systems.size} systems, ${state.empires.size} empires, ${state.ships.size} ships.`,
  );

  return {
    destroy(): void {
      renderRunning = false;
      simLoop.stop();
      // v4M1.10 — detach focus-loss handlers so a teardown-while-blurred
      // doesn't leave a stale closure call against a dead controller.
      window.removeEventListener('blur', onWindowBlur);
      window.removeEventListener('focus', onWindowFocus);
      // In-game menu may be open when the user clicked Return to Main Menu;
      // tear it down before the rest of the session so it doesn't survive
      // the transition as an orphan overlay.
      if (inGameMenu !== null) {
        inGameMenu.destroy();
        inGameMenu = null;
      }
      // v3M12.2 — same treatment for the end-game overlay if it's
      // still mounted (e.g. session teardown via the unload path
      // rather than the overlay's own Return-to-Main-Menu button).
      if (endGameOverlay !== null) {
        endGameOverlay.destroy();
        endGameOverlay = null;
      }
      input.destroy();
      researchPanel.destroy();
      devToolbox.destroy();
      fleetPanel.destroy();
      jpTooltip.destroy();
      fleetCompositionTooltip.destroy();
      miniGalaxy.destroy();
      panel.destroy();
      speedWidget.destroy();
      overlayToggle.destroy();
      alertFeed.destroy();
      hud.destroy();
      // v2M8.7 — fade out the ambient bed (1 s ramp). Engine drops
      // its ambientBed handle synchronously so a fast Return-to-Menu
      // → New-Game round-trip doesn't double-instance.
      getAudioEngine().stopAmbientBed();
      // v4M1.7 — wipe any in-flight combat-overlay bed so the engine
      // doesn't carry stale state across the session boundary. Also
      // stop any victory / defeat bed the end-game stinger lifted —
      // those live past session.destroy if not torn down here.
      tearDownCombatOverlay(getAudioEngine(), combatOverlayState);
      getAudioEngine().stopMusicBed('victory', 0.6);
      getAudioEngine().stopMusicBed('defeat', 0.6);
      // Clear debug globals so the old state can GC.
      const w = window as unknown as Record<string, unknown>;
      delete w.__state;
      delete w.__ui;
      // Repaint the canvas to a blank frame so the torn-down session doesn't
      // leave its last render visible under the main menu overlay.
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.restore();
      }
    },
  };
}
